import { CollectorDTO } from "api";
import { isNullOrUndefined } from "utils";
import { getCollectorString } from "pages/authenticated/documents/utils";

/**
 * Filters collectors to only include those with valid account number or meter number
 * @param collectors - Array of collectors to filter
 * @returns Filtered array of valid collectors
 */
export const getValidCollectors = (collectors?: CollectorDTO[]): CollectorDTO[] => {
    if (!collectors) {
        return [];
    }
    return collectors.filter(
        (item: CollectorDTO) => !isNullOrUndefined(item.accountNumber) || !isNullOrUndefined(item.meterNumber)
    );
};

/**
 * Generates collector options strings for autocomplete
 * @param collectors - Array of collectors
 * @returns Array of unique collector string representations
 */
export const getCollectorsOptions = (collectors?: CollectorDTO[]): string[] => {
    const validCollectors = getValidCollectors(collectors);
    const options = validCollectors.map(getCollectorString);
    return Array.from(new Set(options));
};

/**
 * Finds a collector by its ID
 * @param collectors - Array of collectors to search
 * @param collectorId - ID of the collector to find
 * @returns The found collector or undefined
 */
export const findCollectorById = (
    collectors?: CollectorDTO[],
    collectorId?: number
): CollectorDTO | undefined => {
    if (!collectors || !collectorId) {
        return undefined;
    }
    return collectors.find((c: CollectorDTO) => c.id === collectorId);
};

/**
 * Finds a collector by its string representation
 * @param collectors - Array of collectors to search
 * @param collectorString - String representation of the collector
 * @param compareCollector - Function to compare collector string with collector object
 * @returns The found collector or undefined
 */
export const findCollectorByString = (
    collectors?: CollectorDTO[],
    collectorString?: string | null,
    compareCollector?: (collectorString: string, collector: CollectorDTO) => boolean
): CollectorDTO | undefined => {
    if (!collectors || !collectorString || !compareCollector) {
        return undefined;
    }
    return collectors.find((c: CollectorDTO) => compareCollector(collectorString, c));
};

/**
 * Gets notes from a collector by ID
 * @param collectors - Array of collectors to search
 * @param collectorId - ID of the collector (can be number, null, or undefined)
 * @returns The collector's notes or empty string
 */
export const getCollectorNotes = (
    collectors?: CollectorDTO[],
    collectorId?: number | null
): string => {
    if (collectorId === null || collectorId === undefined) {
        return "";
    }
    const collector = findCollectorById(collectors, collectorId);
    return collector?.notes || "";
};

